import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Input, Select, useToast } from '@w7/ui'
import { useState, type FormEvent } from 'react'
import { cmApi, type SecurityUserWhitelistRow } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { enabledChip } from './securityUi'
import { SecurityCrudPage } from './SecurityCrudPage'

export function WhitelistUsersPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const [kind, setKind] = useState<'ip' | 'device'>('ip')
  const [value, setValue] = useState('')
  const [username, setUsername] = useState('')
  const [comment, setComment] = useState('')

  const saveM = useMutation({
    mutationFn: () =>
      cmApi.securityWhitelistUsersSave(authHeaders(), {
        kind,
        value: value.trim(),
        username: username.trim() || undefined,
        comment: comment.trim() || undefined,
      }),
    onSuccess: async () => {
      toast.success('Whitelist entry saved')
      setValue('')
      setUsername('')
      setComment('')
      await qc.invalidateQueries({ queryKey: ['cm', 'security', 'whitelist-users'] })
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : String(err)),
  })

  return (
    <SecurityCrudPage<SecurityUserWhitelistRow>
      title="Whitelist users"
      subtitle="Exception allowlist by IP or device"
      queryKey="whitelist-users"
      emptyHint="No user whitelist rows yet."
      listFn={(h, body) => cmApi.securityWhitelistUsersList(h, body)}
      deleteFn={cmApi.securityWhitelistUsersDelete}
      columns={[
        { key: 'kind', header: 'Kind', cell: (r) => r.kind },
        { key: 'value', header: 'Value', cell: (r) => r.value },
        { key: 'user', header: 'Username', cell: (r) => r.username ?? '—' },
        { key: 'comment', header: 'Comment', cell: (r) => r.comment ?? '—' },
        { key: 'enabled', header: 'Enabled', cell: (r) => enabledChip(r.enabled) },
      ]}
      form={
        <form
          className="cm-form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            saveM.mutate()
          }}
        >
          <div className="cm-form-grid">
            <Field label="Kind" required>
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={kind}
                  onChange={(e) => setKind(e.target.value === 'device' ? 'device' : 'ip')}
                  options={[
                    { value: 'ip', label: 'IP' },
                    { value: 'device', label: 'Device' },
                  ]}
                />
              )}
            </Field>
            <Field label={kind === 'ip' ? 'IP' : 'Device ID'} required>
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  required
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={kind === 'ip' ? 'IP' : 'Device ID'}
                />
              )}
            </Field>
            <Field label="Username">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Optional"
                />
              )}
            </Field>
            <Field label="Comment">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Optional"
                />
              )}
            </Field>
            <div className="cm-form-actions">
              <Button type="submit" busy={saveM.isPending}>
                Add / update
              </Button>
            </div>
          </div>
        </form>
      }
    />
  )
}
